import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {

  private secret_key = 'ev_charger_app_2024_secure_key'; // In production, use environment variable

  constructor() { }

  // Simple encryption using base64 and character shifting
  encrypt(data: string): string {
    try {
      if (!data) return '';
      
      // Convert to base64 first
      const base64_data = btoa(unescape(encodeURIComponent(data)));
      
      // Apply character shifting
      let encrypted = '';
      for (let i = 0; i < base64_data.length; i++) {
        const char_code = base64_data.charCodeAt(i);
        const shifted = char_code + (i % 10) + 3; // Simple shift
        encrypted += String.fromCharCode(shifted);
      }
      
      return btoa(encrypted);
    } catch (error) {
      return data; // Return original if encryption fails
    }
  }

  // Decrypt the encrypted data
  decrypt(encrypted_data: string): string {
    try {
      if (!encrypted_data) return '';
      
      // Decode from base64
      const decoded = atob(encrypted_data);
      
      // Reverse character shifting
      let decrypted = '';
      for (let i = 0; i < decoded.length; i++) {
        const char_code = decoded.charCodeAt(i);
        const shifted = char_code - (i % 10) - 3; // Reverse shift
        decrypted += String.fromCharCode(shifted);
      }
      
      // Decode from base64
      return decodeURIComponent(escape(atob(decrypted)));
    } catch (error) {
      return ''; // Return empty string if decryption fails
    }
  }

  // Secure localStorage methods
  set_secure_item(key: string, value: any): void {
    try {
      const json_string = JSON.stringify(value);
      const encrypted = this.encrypt(json_string);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      // Fallback to regular localStorage if encryption fails
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  get_secure_item(key: string): any {
    try {
      const encrypted_data = localStorage.getItem(key);
      if (!encrypted_data) return null;
      
      const decrypted = this.decrypt(encrypted_data);
      return JSON.parse(decrypted);
    } catch (error) {
      // Try to parse as regular JSON if decryption fails
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch {
        return null;
      }
    }
  }

  // Remove item from localStorage
  remove_secure_item(key: string): void {
    localStorage.removeItem(key);
  }

  // Clear all secure items
  clear_all_secure_items(): void {
    localStorage.clear();
  }

  // Generate session token
  generate_session_token(): string {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2);
    return this.encrypt(timestamp + '_' + random);
  }

  // Validate session token
  validate_session_token(token: string, max_age_ms: number = 3600000): boolean {
    try {
      const decrypted = this.decrypt(token);
      const [timestamp] = decrypted.split('_');
      const token_age = Date.now() - parseInt(timestamp);
      
      return token_age < max_age_ms;
    } catch {
      return false;
    }
  }
}

